import React, { useState, useEffect, useRef } from 'react';
import { Search, User as UserIcon, ArrowLeft, Menu, X, Home as HomeIcon, Info, Mail, Bell, Heart, ShoppingCart, LogOut, Mic, ChevronDown, LayoutDashboard, Store } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { StoreConfig } from '../../types';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth, loginWithGoogle } from '../../firebase';
import GoogleLoginButton from './GoogleLoginButton';
import { translateVoiceAsync } from '../../utils/dataProcessor';

interface NavbarProps {
  onSearch: (query: string) => void;
  config?: StoreConfig;
  onOpenOrders?: () => void;
  favoritesOnly?: boolean;
  onToggleFavoritesOnly?: () => void;
  favoritesCount?: number;
  cartItemsCount?: number;
  cartTotal?: number;
  onOpenCart?: () => void;
  hideGoogleLogin?: boolean;
  hideBackButton?: boolean;
  searchQuery?: string;
}

interface SearchBarProps {
  value: string;
  onChange: (val: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  className?: string;
}

const HINDI_TO_ENGLISH_DICTIONARY: Record<string, string> = {
  // Devanagari (Hindi Script)
  'तेल': 'oil',
  'साबुन': 'soap',
  'क्रीम': 'cream',
  'चावल': 'rice',
  'दाल': 'dal',
  'आटा': 'flour',
  'चीनी': 'sugar',
  'नमक': 'salt',
  'हल्दी': 'turmeric',
  'मिर्च': 'chilli',
  'मिर्ची': 'chilli',
  'जीरा': 'cumin',
  'धनिया': 'coriander',
  'चाय': 'tea',
  'दूध': 'milk',
  'दही': 'curd',
  'घी': 'ghee',
  'मक्खन': 'butter',
  'बिस्किट': 'biscuit',
  'लिपस्टिक': 'lipstick',
  'काजल': 'kajal',
  'मेहंदी': 'mehndi',
  'मेंहदी': 'mehndi',
  'बादाम': 'almond',
  'काजू': 'cashew',
  'किशमिश': 'raisins',
  'अदरक': 'ginger',
  'लहसुन': 'garlic',
  'टमाटर': 'tomato',
  'आलू': 'potato',
  'प्याज': 'onion',
  'नींबू': 'lemon',
  'धूप': 'incense',
  'अगरबत्ती': 'incense',
  'कपूर': 'camphor',
  'नारीयल': 'coconut',
  'नारियल': 'coconut',
  'पानी': 'water',
  'फूल': 'flower',
  'शुद्ध': 'pure',
  'काला': 'black',
  'लाल': 'red',
  'हरा': 'green',
  'पीला': 'yellow',
  'सफेद': 'white',
  'गुलाब': 'rose',
  'चंदन': 'sandalwood',
  'केसर': 'saffron',
  'इलायची': 'cardamom',
  'लौंग': 'clove',
  'हींग': 'asafoetida',

  // Hinglish / Phonetic Pronunciations
  'tel': 'oil',
  'tail': 'oil',
  'sabun': 'soap',
  'saabun': 'soap',
  'chawal': 'rice',
  'chaval': 'rice',
  'daal': 'dal',
  'atta': 'flour',
  'aata': 'flour',
  'aatta': 'flour',
  'chini': 'sugar',
  'cheeni': 'sugar',
  'namak': 'salt',
  'haldi': 'turmeric',
  'mirch': 'chilli',
  'mirchi': 'chilli',
  'jeera': 'cumin',
  'dhaniya': 'coriander',
  'chai': 'tea',
  'dudh': 'milk',
  'doodh': 'milk',
  'dahi': 'curd',
  'makhhan': 'butter',
  'makhan': 'butter',
  'biscuit': 'biscuit',
  'biscut': 'biscuit',
  'badam': 'almond',
  'kaju': 'cashew',
  'kishmish': 'raisins',
  'adrak': 'ginger',
  'lahsun': 'garlic',
  'lehsun': 'garlic',
  'tamatar': 'tomato',
  'aloo': 'potato',
  'alu': 'potato',
  'pyaz': 'onion',
  'pyaaz': 'onion',
  'nimbu': 'lemon',
  'dhoop': 'incense',
  'agarbatti': 'incense',
  'kapoor': 'camphor',
  'kapur': 'camphor',
  'nariyal': 'coconut',
  'pani': 'water',
  'phool': 'flower',
  'shudh': 'pure',
  'shuddh': 'pure',
  'kala': 'black',
  'lal': 'red',
  'hara': 'green',
  'peela': 'yellow',
  'pila': 'yellow',
  'safed': 'white',
  'gulab': 'rose',
  'chandan': 'sandalwood',
  'kesar': 'saffron',
  'elaichi': 'cardamom',
  'laung': 'clove',
  'hing': 'asafoetida'
};

const translateHindiToEnglish = (text: string): { original: string; translated: string; changed: boolean } => {
  if (!text) return { original: text, translated: text, changed: false };
  
  const parts = text.split(/(\s+)/);
  let changed = false;
  
  const translatedParts = parts.map(part => {
    if (/^\s+$/.test(part)) return part;
    
    const cleanWord = part.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?]/g, "");
    const mappedValue = HINDI_TO_ENGLISH_DICTIONARY[cleanWord];
    
    if (mappedValue) {
      changed = true;
      return mappedValue;
    }
    return part;
  });
  
  return {
    original: text,
    translated: translatedParts.join(""),
    changed
  };
};

const SearchBar = ({ value, onChange, onSubmit, className = "" }: SearchBarProps) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [lang, setLang] = useState<'en-IN' | 'hi-IN'>('en-IN');
  const [recognition, setRecognition] = useState<any>(null);
  const [notice, setNotice] = useState<{ text: string; isError?: boolean } | null>(null);

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => {
        setNotice(null);
      }, notice.isError ? 6000 : 4000);
      return () => clearTimeout(timer);
    }
  }, [notice]);

  useEffect(() => {
    const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognitionClass) {
      setIsSupported(true);
      const rec = new SpeechRecognitionClass();
      rec.continuous = false;
      rec.interimResults = false;
      setRecognition(rec);
    }
  }, []);

  const startListening = () => {
    if (!recognition) return;

    recognition.lang = lang;
    
    recognition.onstart = () => {
      setIsListening(true);
      setNotice(null);
    };

    recognition.onresult = async (event: any) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        try {
          // Offload voice search translation to Web Worker
          const result = await translateVoiceAsync(transcript);
          if (result.changed) {
            onChange(result.translated);
            setNotice({
              text: `✨ Translated: "${result.original}" ➔ "${result.translated}"`,
              isError: false
            });
          } else {
            onChange(transcript);
          }
        } catch (err) {
          console.error('[SpeechRecognition] Web Worker translation failed:', err);
          // Fallback to direct client mapping if worker failed
          const result = translateHindiToEnglish(transcript);
          if (result.changed) {
            onChange(result.translated);
            setNotice({
              text: `✨ Translated (Fallback): "${result.original}" ➔ "${result.translated}"`,
              isError: false
            });
          } else {
            onChange(transcript);
          }
        }
      }
    };

    recognition.onerror = (event: any) => {
      console.error('[SpeechRecognition] Error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        setNotice({
          text: '🎙️ Microphone access denied! Please allow mic permission in your browser or click "Open in new tab" / माइक अनुमति दें या नए टैब में खोलें।',
          isError: true
        });
      } else {
        setNotice({
          text: `🎙️ Voice search error: ${event.error}. Please try again / कृपया फिर से कोशिश करें।`,
          isError: true
        });
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      // Auto-trigger the search query when speaking stops
      onSubmit();
    };

    try {
      recognition.start();
    } catch (err) {
      console.error('[SpeechRecognition] Failed to start:', err);
    }
  };

  const stopListening = () => {
    if (!recognition) return;
    try {
      recognition.stop();
    } catch (err) {
      console.error('[SpeechRecognition] Failed to stop:', err);
    }
  };

  const toggleListening = () => {
    if (!isSupported) {
      setNotice({
        text: '🎙️ Voice search is not supported on this browser. Try Chrome, Safari, or Edge! / आपके ब्राउज़र पर आवाज़ से खोजना समर्थित नहीं है।',
        isError: true
      });
      return;
    }
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <form 
      onSubmit={onSubmit}
      className={`relative flex items-center bg-white/40 backdrop-blur-xl rounded-2xl border border-white/50 focus-within:border-brand-accent focus-within:bg-white/60 focus-within:shadow-[0_8px_30px_rgb(0,0,0,0.08)] focus-within:ring-4 focus-within:ring-brand-accent/20 transition-all duration-300 p-1 md:p-1.5 shadow-sm ${className}`}
    >
      <div className="flex items-center pl-3 pr-2 flex-1 min-w-0 py-0.5">
        <Search className="w-4 h-4 text-gray-400 mr-2.5 shrink-0 transition-colors group-focus-within:text-brand-accent" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isListening ? "Listening... / बोलें..." : "Search price for..."}
          className={`bg-transparent border-none outline-none w-full text-xs md:text-sm font-sans min-w-0 text-gray-800 placeholder-gray-400 font-medium ${isListening ? 'placeholder-red-500 text-red-600 font-bold animate-pulse' : ''}`}
        />
        
        <div className="flex items-center gap-2 ml-2 shrink-0">
          {/* Clear button when value is present */}
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition-colors cursor-pointer"
              title="Clear search / साफ़ करें"
              aria-label="Clear search / साफ़ करें"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Language toggle: Hindi or English */}
          {isSupported && (
            <button
              type="button"
              onClick={() => setLang(prev => prev === 'hi-IN' ? 'en-IN' : 'hi-IN')}
              className={`text-[9px] font-black px-2 py-1 rounded-lg transition-all duration-200 cursor-pointer ${
                lang === 'hi-IN' 
                  ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                  : 'bg-indigo-50 text-indigo-700 border border-indigo-100'
              }`}
              title="Toggle Language / भाषा बदलें"
              aria-label="Toggle Language / भाषा बदलें"
            >
              {lang === 'hi-IN' ? 'हिन्दी' : 'EN'}
            </button>
          )}
          
          <button
            type="button"
            onClick={toggleListening}
            className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-300 relative cursor-pointer border ${
              isListening 
                ? 'bg-red-500 text-white border-red-600 shadow-[0_0_12px_rgba(239,68,68,0.4)] animate-pulse' 
                : 'bg-white hover:bg-gray-100 text-gray-500 hover:text-brand-accent border-gray-200/60 shadow-xs'
            }`}
            title={isListening ? "Stop listening / सुनना बंद करें" : "Voice Search / आवाज़ से खोजें"}
            aria-label={isListening ? "Stop listening / सुनना बंद करें" : "Voice Search / आवाज़ से खोजें"}
          >
            <Mic className={`w-4 h-4 ${isListening ? 'scale-110 animate-bounce' : ''}`} />
            {isListening && (
              <>
                <span className="absolute -inset-1 rounded-xl border border-red-500 animate-ping opacity-75" />
                <span className="absolute -inset-2 rounded-xl border border-red-400 animate-ping opacity-40 delay-150" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Floating status / translation badge */}
      {notice && (
        <div className={`absolute top-full left-0 right-0 mt-2 text-white text-[10px] md:text-xs py-2.5 px-4 rounded-xl shadow-lg z-50 flex items-center justify-between border ${
          notice.isError 
            ? 'bg-red-600 border-red-700 animate-shake' 
            : 'bg-brand-accent border-emerald-800 animate-bounce shadow-xl'
        }`}>
          <span className="font-semibold">{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} className="ml-2 font-bold hover:opacity-75 text-sm leading-none" aria-label="Close notice / सूचना बंद करें">×</button>
        </div>
      )}
    </form>
  );
};

interface NavLinksProps {
  closeMenu: () => void;
  favoritesOnly?: boolean;
  onToggleFavoritesOnly?: () => void;
  favoritesCount?: number;
}

const NavLinks = ({ closeMenu, favoritesOnly, onToggleFavoritesOnly, favoritesCount = 0 }: NavLinksProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSubPage = location.pathname !== '/';

  const handleFavoritesClick = () => {
    closeMenu();
    if (isSubPage) {
      navigate('/?filter=favorites');
    } else {
      onToggleFavoritesOnly?.();
    }
  };

  return (
    <>
      <Link 
        to="/" 
        onClick={() => {
          closeMenu();
          if (!isSubPage && favoritesOnly) {
            onToggleFavoritesOnly?.();
          }
        }} 
        className="flex items-center gap-2 text-sm font-semibold hover:text-brand-accent transition-colors"
      >
        <HomeIcon className="w-4 h-4 lg:hidden" />
        <span>Home</span>
      </Link>

      <button
        type="button"
        onClick={handleFavoritesClick}
        className={`flex items-center gap-2 text-sm font-semibold transition-colors cursor-pointer text-left ${
          favoritesOnly && !isSubPage
            ? 'text-red-500 font-bold'
            : 'hover:text-brand-accent text-brand-text'
        }`}
      >
        <Heart className={`w-4 h-4 ${favoritesOnly && !isSubPage ? 'fill-red-500 text-red-500' : ''}`} />
        <span>My Favorites {favoritesCount > 0 && `(${favoritesCount})`}</span>
      </button>

      <Link to="/about" onClick={closeMenu} className="flex items-center gap-2 text-sm font-semibold hover:text-brand-accent transition-colors">
        <Info className="w-4 h-4 lg:hidden" />
        <span>About</span>
      </Link>
    </>
  );
};

export default function Navbar({ 
  onSearch, 
  config, 
  onOpenOrders,
  favoritesOnly = false,
  onToggleFavoritesOnly,
  favoritesCount = 0,
  cartItemsCount,
  cartTotal,
  onOpenCart,
  hideGoogleLogin = false,
  hideBackButton = false,
  searchQuery = ''
}: NavbarProps) {
  const [searchValue, setSearchValue] = useState(searchQuery);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsAdminDropdownOpen(false);
      }
    };
    if (isAdminDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAdminDropdownOpen]);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login';

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      const adminEmails = ['tarzanmaurya1234@gmail.com', 'rajkiranastore0123@gmail.com'];
      if (currentUser) {
        try {
          let isAdminCheck = false;
          if (currentUser.email && adminEmails.includes(currentUser.email.toLowerCase())) {
            isAdminCheck = true;
          } else if (currentUser.email) {
            try {
              const idToken = await currentUser.getIdToken();
              const res = await fetch('/api/check-admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken })
              });
              const data = await res.json();
              if (data && data.isAdmin) {
                isAdminCheck = true;
              }
            } catch (fetchErr) {
              console.warn('[NavbarAuthCheck] Backend API note:', fetchErr);
            }
          }
          setIsUserAdmin(isAdminCheck);
        } catch (err) {
          console.error('[NavbarAuthCheck] Error verifying admin status:', err);
          setIsUserAdmin(!!(currentUser.email && adminEmails.includes(currentUser.email.toLowerCase())));
        }
      } else {
        setIsUserAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  // Dynamic logo - removed fallback
  const LOGO_URL = config?.logoUrl;

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    onSearch(searchValue);
  };

  const handleSearchChange = (val: string) => {
    setSearchValue(val);
    // Instant search while typing for better UX
    onSearch(val);
  };

  const isSubPage = location.pathname !== '/';

  return (
    <>
      <nav className="h-16 border-b border-white/40 flex items-center justify-between px-4 md:px-8 bg-white/40 backdrop-blur-xl sticky top-0 z-50 shadow-[0_4px_30px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-2 lg:gap-4 h-full">
          {isSubPage && !isAdminRoute && !hideBackButton && (
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-1"
              title="Go Back"
              aria-label="Go Back / पीछे जाएं"
            >
              <ArrowLeft className="w-5 h-5 text-brand-accent" />
            </button>
          )}

          <Link to="/" className="flex items-center gap-2 group mr-2 h-full">
            <div className={`w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-brand-accent bg-white flex-shrink-0 p-1 shadow-md flex items-center justify-center overflow-hidden transition-all duration-500`}>
               {LOGO_URL ? (
                 <img 
                   src={LOGO_URL} 
                   alt="Raj Kirana Store Logo / राज किराना स्टोर लोगो" 
                   width={40}
                   height={40}
                   fetchPriority="high"
                   className="max-w-full max-h-full object-contain"
                 />
               ) : (
                 <div className="w-full h-full bg-brand-accent/5 flex items-center justify-center">
                    <span className="text-base md:text-lg font-black text-brand-accent italic tracking-tighter">RK</span>
                 </div>
               )}
            </div>
            <div className="flex flex-col justify-center min-w-0 max-w-[150px] md:max-w-[300px]">
              <span className="logo font-display font-bold text-base md:text-xl text-brand-accent tracking-tighter whitespace-nowrap leading-none">
                Raj Kirana Store
              </span>
            </div>
          </Link>
        </div>

        {!isSubPage && (
          <div className="flex-1 max-w-sm mx-10 hidden lg:block">
            <SearchBar value={searchValue} onChange={handleSearchChange} onSubmit={handleSearchSubmit} />
          </div>
        )}

        <div className="flex items-center gap-2 md:gap-6">
          <div className="hidden lg:flex items-center gap-6">
            <NavLinks 
              closeMenu={() => setIsMenuOpen(false)} 
              favoritesOnly={favoritesOnly}
              onToggleFavoritesOnly={onToggleFavoritesOnly}
              favoritesCount={favoritesCount}
            />
          </div>

          {cartItemsCount !== undefined && (
            <button
              onClick={onOpenCart}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all duration-300 cursor-pointer active:scale-95 shadow-sm group shrink-0 ${
                cartItemsCount > 0
                  ? 'bg-indigo-600 border-indigo-700 text-white shadow-md shadow-indigo-600/10 hover:bg-indigo-700'
                  : 'bg-gray-50/50 hover:bg-gray-100/80 border-gray-200 text-gray-700 hover:text-indigo-600 hover:border-indigo-200'
              }`}
              title="View Cart"
              aria-label="View Cart / कार्ट देखें"
            >
              <div className="relative shrink-0">
                <ShoppingCart className={`w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${
                  cartItemsCount > 0 ? 'text-white' : 'text-gray-600 group-hover:text-indigo-600'
                }`} />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[8px] font-black min-w-[15px] h-3.5 px-0.5 rounded-full flex items-center justify-center border border-indigo-600">
                    {cartItemsCount}
                  </span>
                )}
              </div>
              {(cartTotal !== undefined && cartTotal > 0) ? (
                <span className={`text-xs font-extrabold tracking-tight shrink-0 ${
                  cartItemsCount > 0 ? 'text-white' : 'text-gray-800'
                }`}>
                  ₹{cartTotal}
                </span>
              ) : (
                <span className={`text-xs font-extrabold tracking-wide hidden sm:inline shrink-0 ${
                  cartItemsCount > 0 ? 'text-white' : 'text-gray-600 group-hover:text-indigo-600'
                }`}>
                  Cart
                </span>
              )}
            </button>
          )}

          {user ? (
            isUserAdmin ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/20 rounded-xl transition-all text-amber-800 cursor-pointer active:scale-95 text-xs font-bold shrink-0 shadow-sm"
                  aria-label="Admin panel quick menu / एडमिन पैनल त्वरित मेनू"
                >
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || 'Admin'} width={20} height={20} loading="lazy" className="w-5 h-5 rounded-full border border-amber-500 shrink-0" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center border border-amber-500/20 shrink-0">
                      <UserIcon className="w-3 h-3 text-amber-800" />
                    </div>
                  )}
                  <span className="max-w-[120px] truncate font-extrabold uppercase tracking-wide">
                    Admin / एडमिन
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-amber-700 transition-transform duration-200 shrink-0 ${isAdminDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                </button>
                {isAdminDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 overflow-hidden animate-fade-in">
                    <div className="px-4 py-2 border-b border-gray-50">
                      <p className="text-xs text-gray-500 font-medium truncate">{user.email}</p>
                      <span className="inline-block mt-1 text-[10px] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider font-mono">
                        Store Admin
                      </span>
                    </div>
                    {location.pathname.startsWith('/admin') ? (
                      <Link
                        to="/"
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium border-b border-gray-50"
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        <Store className="w-4 h-4 text-emerald-600" />
                        <span>View Shop / शॉप देखें</span>
                      </Link>
                    ) : (
                      <Link
                        to="/admin"
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium border-b border-gray-50"
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4 text-amber-500" />
                        <span>Dashboard / एडमिन पैनल</span>
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        auth.signOut().then(() => {
                          localStorage.removeItem('adm_auth_session');
                          window.location.href = window.location.pathname.startsWith('/admin') ? '/admin' : '/';
                        });
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors font-medium"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout / लॉगआउट
                    </button>
                  </div>
                )}
              </div>
            ) : isAdminRoute ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                  className="flex items-center justify-center w-9 h-9 hover:bg-gray-50 border border-transparent hover:border-gray-200 rounded-full transition-all cursor-pointer shadow-sm active:scale-95"
                  aria-label="Admin settings dropdown / एडमिन सेटिंग्स मेनू"
                >
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || 'User'} width={32} height={32} loading="lazy" className="w-8 h-8 rounded-full border border-gray-200 object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-brand-accent/10 flex items-center justify-center border border-brand-accent/20">
                      <UserIcon className="w-4 h-4 text-brand-accent" />
                    </div>
                  )}
                </button>
                {isAdminDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 overflow-hidden">
                    <div className="px-4 py-2 border-b border-gray-50">
                      <p className="text-xs text-gray-500 font-medium truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        auth.signOut().then(() => {
                          localStorage.removeItem('adm_auth_session');
                          window.location.href = window.location.pathname.startsWith('/admin') ? '/admin' : '/';
                        });
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors font-medium"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={onOpenOrders}
                className="flex items-center gap-1.5 px-3 py-2 bg-brand-accent/5 hover:bg-brand-accent/10 border border-brand-accent/20 rounded-xl transition-all text-brand-accent cursor-pointer active:scale-95 text-xs font-bold shrink-0 shadow-sm"
              >
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.displayName || 'User'} width={20} height={20} loading="lazy" className="w-5 h-5 rounded-full border border-brand-accent shrink-0" referrerPolicy="no-referrer" />
                ) : (
                  <UserIcon className="w-4 h-4 text-brand-accent shrink-0" />
                )}
                <span className="max-w-[100px] truncate">
                  My Orders
                </span>
                <span className="flex h-1.5 w-1.5 relative shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
              </button>
            )
          ) : !hideGoogleLogin && (
            <GoogleLoginButton variant="navbar" />
          )}
        </div>
      </nav>

      {/* Mobile Menu Overlay - Bottom Sheet Style */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 lg:hidden animate-fade-in" onClick={() => setIsMenuOpen(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 shadow-2xl animate-slide-up max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-6" />
            <div className="flex flex-col gap-6">
              <NavLinks 
                closeMenu={() => setIsMenuOpen(false)} 
                favoritesOnly={favoritesOnly}
                onToggleFavoritesOnly={onToggleFavoritesOnly}
                favoritesCount={favoritesCount}
              />
              
              {user ? (
                isUserAdmin ? (
                  <div className="flex flex-col gap-3 w-full">
                    {location.pathname.startsWith('/admin') ? (
                      <Link
                        to="/"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-2.5 w-full bg-emerald-600 hover:bg-emerald-700 text-white p-4 rounded-xl font-bold text-sm shadow-md active:scale-95 transition-transform"
                      >
                        <Store className="w-5 h-5 shrink-0" />
                        <span>View Shop / शॉप देखें</span>
                      </Link>
                    ) : (
                      <Link
                        to="/admin"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-2.5 w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-4 rounded-xl font-bold text-sm shadow-md active:scale-95 transition-transform"
                      >
                        <LayoutDashboard className="w-5 h-5 shrink-0" />
                        <span>Dashboard / एडमिन पैनल</span>
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        auth.signOut().then(() => {
                          localStorage.removeItem('adm_auth_session');
                          window.location.href = window.location.pathname.startsWith('/admin') ? '/admin' : '/';
                        });
                      }}
                      className="flex items-center justify-center gap-2 w-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 p-4 rounded-xl font-bold text-sm"
                    >
                      <LogOut className="w-5 h-5 shrink-0" />
                      <span>Logout Admin / लॉगआउट</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onOpenOrders?.();
                    }}
                    className="flex items-center justify-center gap-2.5 w-full bg-brand-accent text-white p-4 rounded-xl font-bold text-sm shadow-lg active:scale-95 transition-transform cursor-pointer"
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="Avatar" width={20} height={20} loading="lazy" className="w-5 h-5 rounded-full border border-white" referrerPolicy="no-referrer" />
                    ) : (
                      <UserIcon className="w-5 h-5" />
                    )}
                    <span>My Orders</span>
                  </button>
                )
              ) : !hideGoogleLogin && (
                <button
                  onClick={async () => {
                    setIsMenuOpen(false);
                    try {
                      await loginWithGoogle();
                    } catch (err: any) {
                      if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
                        console.error('Navbar Google login failed (mobile):', err);
                      }
                    }
                  }}
                  className="flex items-center justify-center gap-2.5 w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 p-4 rounded-xl font-bold text-sm shadow-md active:scale-95 transition-transform cursor-pointer"
                >
                  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  <span>Login with Google / गूगल लॉगिन</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile/Tablet Search Bar - Visible below navbar on smaller screens */}
      {!isSubPage && (
        <div className="lg:hidden bg-white/40 backdrop-blur-xl border-b border-white/40 p-3 px-4 md:px-10 sticky top-16 z-40 animate-fade-in shadow-[0_4px_30px_rgba(0,0,0,0.05)]">
          <SearchBar value={searchValue} onChange={handleSearchChange} onSubmit={handleSearchSubmit} />
        </div>
      )}
    </>
  );
}
